import { Group, Popover, UnstyledButton, VisuallyHidden } from "@mantine/core";
import { IconCheck, IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import clsx from "clsx";
import { useState } from "react";

import styles from "./StepperBar.module.css";

/** A step in the breadcrumb rail. `current` shows a green dot; `completed` a check; `upcoming` plain. */
export type CrumbState = "completed" | "current" | "upcoming";

export interface StepCrumb {
    label: string;
    state: CrumbState;
}

/** A beat row in the dropdown menu. */
export type BeatMenuStatus = "covered" | "skipped" | "pending";

export interface StepMenuBeat {
    /** Stable beat id — the scroll-anchor target and row key. */
    id: string;
    label: string;
    required?: boolean;
    status: BeatMenuStatus;
    /** The beat currently being worked — highlighted in the menu. */
    current?: boolean;
}

export interface StepperBarProps {
    sellLabel?: string;
    crumbs: StepCrumb[];
    beats: StepMenuBeat[];
    /** Jump the narrative to a beat (scroll only — does not change the active step). */
    onBeatSelect?: (beatId: string) => void;
    /** Render the dropdown open (mainly for Storybook / review). */
    defaultMenuOpen?: boolean;
}

const crumbStateClass: Record<CrumbState, string> = {
    completed: styles.crumbCompleted,
    current: styles.crumbCurrent,
    upcoming: styles.crumbUpcoming,
};

// The rail is an ordered list so assistive tech announces step order and count. Each crumb carries a
// visually-hidden state word ("Completed"/"Upcoming") next to its decorative glyph, and the current
// crumb is marked `aria-current="step"` — so completion/position is conveyed as text, not by colour
// or an unlabelled icon/dot alone. The chevron separators and status glyphs are purely decorative and
// hidden from the accessibility tree.
const Breadcrumb = ({ crumbs }: { crumbs: StepCrumb[] }) => (
    <Group component="ol" gap={8} wrap="nowrap" className={styles.rail} aria-label="Sell progress">
        {crumbs.map((crumb, i) => (
            <li key={`${crumb.label}-${i}`} className={styles.crumbItem}>
                {i > 0 && <IconChevronRight size={12} className={styles.sep} aria-hidden />}
                <span
                    className={clsx(styles.crumb, crumbStateClass[crumb.state])}
                    aria-current={crumb.state === "current" ? "step" : undefined}
                >
                    {crumb.state === "completed" && (
                        <>
                            <IconCheck size={12} className={styles.check} aria-hidden />
                            <VisuallyHidden>Completed: </VisuallyHidden>
                        </>
                    )}
                    {crumb.state === "current" && <span className={styles.currentDot} aria-hidden />}
                    {crumb.state === "upcoming" && <VisuallyHidden>Upcoming: </VisuallyHidden>}
                    {crumb.label}
                </span>
            </li>
        ))}
    </Group>
);

const StepMenu = ({ beats, onSelect }: { beats: StepMenuBeat[]; onSelect?: (beatId: string) => void }) => (
    <div className={styles.menu}>
        {beats.map((beat) => (
            <UnstyledButton
                key={beat.id}
                className={clsx(styles.row, beat.current && styles.rowCurrent)}
                onClick={() => onSelect?.(beat.id)}
            >
                <span className={styles.rowName}>
                    {beat.label}
                    {beat.required && <span className={styles.req}> (Required)</span>}
                </span>
                {beat.status === "covered" && (
                    <>
                        <IconCheck size={12} className={styles.check} aria-hidden />
                        <VisuallyHidden>Covered</VisuallyHidden>
                    </>
                )}
                {beat.status === "skipped" && <span className={styles.status}>Skipped</span>}
            </UnstyledButton>
        ))}
    </div>
);

export const StepperBar = ({ sellLabel = "Sell", crumbs, beats, onBeatSelect, defaultMenuOpen }: StepperBarProps) => {
    const [opened, setOpened] = useState(defaultMenuOpen ?? false);

    return (
        <Group gap={0} wrap="nowrap">
            <Popover
                opened={opened}
                onChange={setOpened}
                position="bottom-start"
                offset={6}
                classNames={{ dropdown: styles.popoverDropdown }}
            >
                <Popover.Target>
                    <UnstyledButton className={styles.pill} onClick={() => setOpened((o) => !o)}>
                        <span className={styles.pillDot} />
                        <span className={styles.pillLabel}>{sellLabel}</span>
                        <IconChevronDown size={12} className={styles.pillChevron} aria-hidden />
                    </UnstyledButton>
                </Popover.Target>
                <Popover.Dropdown>
                    <StepMenu
                        beats={beats}
                        onSelect={(id) => {
                            onBeatSelect?.(id);
                            setOpened(false);
                        }}
                    />
                </Popover.Dropdown>
            </Popover>

            <span className={styles.divider} />

            <Breadcrumb crumbs={crumbs} />
        </Group>
    );
};
